import React, { useState } from 'react';
import { Button, Tooltip, Dropdown, List, Typography } from 'antd';
import {
  SaveOutlined,
  EditOutlined,
  UndoOutlined,
  RedoOutlined,
  ClearOutlined,
  LoadingOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useToolbarState } from '../../contexts/ToolbarContext';
import { useTableRbac } from '../../hooks/useTableRbac';
import styles from '../../styles/components/FloatingActionBar.module.scss';

// ============================================================================
// Анимация
// ============================================================================

const barVariants = {
  hidden: { opacity: 0, y: 40, x: '-50%' },
  visible: { opacity: 1, y: 0, x: '-50%' },
  exit: { opacity: 0, y: 40, x: '-50%' },
};

const barTransition = {
  type: 'spring' as const,
  stiffness: 400,
  damping: 30,
};

// ============================================================================
// Компонент
// ============================================================================

const FloatingActionBarInner: React.FC = () => {
  const toolbar = useToolbarState();
  const rbac = useTableRbac(toolbar.rbacResource);
  const [clearLoading, setClearLoading] = useState(false);

  const {
    onSave,
    onUndo,
    onRedo,
    onClear,
    changedCells = [],
    saving = false,
    loading = false,
    changesCount = 0,
    saveDisabled = false,
    saveDisabledReason,
    rbacEnabled = true,
    showRbacBadge = true,
  } = toolbar;

  const hasChanges = changesCount > 0;
  const hasTableActions = !!(onUndo || onRedo || onClear);
  const isDisabled = loading || saving;

  // RBAC
  const rbacSaveDisabled = rbacEnabled && rbac.hasResource && !rbac.canWrite;
  const finalSaveDisabled = saveDisabled || rbacSaveDisabled;
  const finalSaveDisabledReason = rbacSaveDisabled
    ? 'У вас нет прав на редактирование данных'
    : saveDisabledReason;

  const shouldShowSave = onSave && (
    !rbacEnabled ||
    !rbac.hasResource ||
    rbac.canWrite
  );

  // Показываем бар когда есть изменения и есть хотя бы save или table actions
  const isVisible = hasChanges && (shouldShowSave || hasTableActions);

  const handleClear = async () => {
    if (!onClear) return;
    setClearLoading(true);
    try {
      await onClear();
      setTimeout(() => setClearLoading(false), 500);
    } catch {
      setClearLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={styles.floatingBar}
          variants={barVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={barTransition}
        >
          {/* Бейдж с количеством изменений */}
          <div className={styles.changesBadge}>
            <EditOutlined style={{ fontSize: 13 }} />
            <span>Несохранённые изменения</span>
            <span className={styles.changesCount}>{changesCount}</span>
          </div>

          <span className={styles.divider} />

          {/* Действия таблицы: Undo / Redo / Clear / ChangedCells */}
          {hasTableActions && (
            <>
              {onUndo && (
                <Tooltip title="Отменить все изменения (вернуть к исходным)">
                  <Button
                    icon={<UndoOutlined />}
                    onClick={onUndo}
                    disabled={isDisabled}
                    size="middle"
                    className={styles.actionBtn}
                  />
                </Tooltip>
              )}

              {onRedo && (
                <Tooltip title="Повторить (Ctrl+Y)">
                  <Button
                    icon={<RedoOutlined />}
                    onClick={onRedo}
                    disabled={isDisabled}
                    size="middle"
                    className={styles.actionBtn}
                  />
                </Tooltip>
              )}

              {onClear && (
                <Tooltip title="Сбросить все изменения">
                  <Button
                    icon={clearLoading ? <LoadingOutlined /> : <ClearOutlined />}
                    onClick={handleClear}
                    disabled={isDisabled || clearLoading}
                    size="middle"
                    className={styles.discardBtn}
                  />
                </Tooltip>
              )}

              {changedCells.length > 0 && (
                <Dropdown
                  popupRender={() => (
                    <div className={styles.changesDropdown}>
                      <div className={styles.changesDropdownHeader}>
                        <Typography.Text strong className={styles.changesDropdownTitle}>
                          Измененные ячейки ({changedCells.length})
                        </Typography.Text>
                      </div>
                      <List
                        size="small"
                        dataSource={changedCells}
                        renderItem={(change) => (
                          <List.Item className={styles.changesListItem}>
                            <div className={styles.changesListItemContent}>
                              <Typography.Text code className={styles.cellCode}>
                                Ячейка {change.row + 1}:{String.fromCharCode(65 + change.col)}
                              </Typography.Text>
                              <Typography.Text className={styles.cellValue}>
                                {String(change.originalValue)} → {String(change.currentValue)}
                              </Typography.Text>
                            </div>
                          </List.Item>
                        )}
                        className={styles.changesListWrapper}
                      />
                    </div>
                  )}
                  placement="top"
                  trigger={['click']}
                >
                  <Tooltip title="Показать измененные ячейки">
                    <Button
                      icon={<EyeOutlined />}
                      disabled={isDisabled}
                      size="middle"
                      className={styles.actionBtn}
                    />
                  </Tooltip>
                </Dropdown>
              )}

              {shouldShowSave && <span className={styles.divider} />}
            </>
          )}

          {/* Кнопка Save */}
          {shouldShowSave && (
            <Tooltip title={finalSaveDisabled ? finalSaveDisabledReason : `Сохранить ${changesCount} изменений`}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={onSave}
                loading={saving}
                disabled={isDisabled || finalSaveDisabled}
                size="middle"
                className={styles.saveBtn}
              >
                Сохранить
              </Button>
            </Tooltip>
          )}

          {/* RBAC индикатор */}
          {showRbacBadge && rbacEnabled && rbac.hasResource && !rbac.canWrite && (
            <Tooltip title="У вас нет прав на запись">
              <span style={{ fontSize: 12, opacity: 0.7 }}>🔒</span>
            </Tooltip>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const FloatingActionBar = React.memo(FloatingActionBarInner);
